import { Injectable } from '@angular/core';

import {
  flattenXmlFieldKeyMapDocument,
  type XmlFieldMapMetaValue,
} from '../xml/xml-field-key-map.flatten';
import xmlFieldKeyMapRaw from '../../../assets/rule-config/field-key-map-xml.json';
import {
  xmlFieldOptionDisplayLabel,
  type XmlFieldMapMeta,
  type XmlFieldOption,
} from '../models/field-option.model';

@Injectable()
export class XmlFieldCatalogService {
  private loaded = false;
  private options: XmlFieldOption[] = [];
  private keyToMeta = new Map<string, XmlFieldMapMeta>();
  private pathToDisplayKey = new Map<string, string>();

  /** Load flattened XML field list once per session. */
  ensureLoaded(): void {
    if (this.loaded) {
      return;
    }
    const flat = flattenXmlFieldKeyMapDocument(xmlFieldKeyMapRaw);
    const collected: XmlFieldOption[] = [];
    for (const [mapKey, raw] of Object.entries(flat)) {
      const key = (mapKey ?? '').toString().trim();
      if (!key) {
        continue;
      }
      const meta: XmlFieldMapMeta =
        typeof raw === 'string' ? { path: raw } : { ...(raw as XmlFieldMapMetaValue) };
      const path = (meta.path ?? '').toString().trim();
      if (!path) {
        continue;
      }
      const displayKey = (meta.displayKey ?? '').trim() || key;
      this.keyToMeta.set(key, meta);
      if (!this.pathToDisplayKey.has(path)) {
        this.pathToDisplayKey.set(path, key);
      }
      collected.push({
        key,
        label: displayKey,
        listLabel: displayKey !== key ? key : undefined,
        path,
        leaf: this.leafFromPath(path),
        isRepeatable: !!meta.isRepeatable,
        repeatableNodes: meta.repeatableNodes?.length ? [...meta.repeatableNodes] : undefined,
        nodePredicates: meta.nodePredicates?.length ? [...meta.nodePredicates] : undefined,
      });
    }
    const byPath = new Map<string, XmlFieldOption>();
    for (const o of collected) {
      if (!byPath.has(o.path)) {
        byPath.set(o.path, o);
      }
    }
    this.options = Array.from(byPath.values()).sort((a, b) =>
      xmlFieldOptionDisplayLabel(a).localeCompare(xmlFieldOptionDisplayLabel(b))
    );
    this.loaded = true;
  }

  getOptions(): XmlFieldOption[] {
    this.ensureLoaded();
    return this.options;
  }

  getMetaByKey(key: string): XmlFieldMapMeta | null {
    this.ensureLoaded();
    const k = (key ?? '').toString().trim();
    return this.keyToMeta.get(k) ?? null;
  }

  /** Exact cartesian / map key (e.g. `TC.TestGrossing.Result.Observation.Name`). */
  findByKey(key: string): XmlFieldOption | undefined {
    this.ensureLoaded();
    const k = (key ?? '').toString().trim();
    if (!k) {
      return undefined;
    }
    const meta = this.keyToMeta.get(k);
    if (!meta?.path) {
      return undefined;
    }
    const displayKey = (meta.displayKey ?? '').trim() || k;
    return {
      key: k,
      label: displayKey,
      listLabel: displayKey !== k ? k : undefined,
      path: meta.path,
      leaf: this.leafFromPath(meta.path),
      isRepeatable: !!meta.isRepeatable,
      repeatableNodes: meta.repeatableNodes?.length ? [...meta.repeatableNodes] : undefined,
      nodePredicates: meta.nodePredicates?.length ? [...meta.nodePredicates] : undefined,
    };
  }

  findByLabel(label: string): XmlFieldOption | undefined {
    this.ensureLoaded();
    const k = (label ?? '').toString().trim();
    if (!k) {
      return undefined;
    }
    const byKey = this.findByKey(k);
    if (byKey) {
      return byKey;
    }
    return this.options.find((o) => o.label === k || o.key === k || o.listLabel === k);
  }

  findByPath(path: string): XmlFieldOption | undefined {
    this.ensureLoaded();
    const p = (path ?? '').toString().trim().replace(/^\/+/, '');
    return this.options.find((o) => o.path === p);
  }

  getDisplayKeyForPath(path: string): string | undefined {
    this.ensureLoaded();
    return this.pathToDisplayKey.get((path ?? '').toString().trim().replace(/^\/+/, ''));
  }

  search(query: string, limit = 250): XmlFieldOption[] {
    this.ensureLoaded();
    const q = this.normalizeSearch(query);
    if (!q) {
      return this.options.slice(0, limit);
    }
    return this.options
      .filter((o) => {
        const hay = [o.key, o.label, o.listLabel ?? '', o.leaf].map((x) => this.normalizeSearch(x)).join(' ');
        return hay.includes(q);
      })
      .sort((a, b) =>
        xmlFieldOptionDisplayLabel(a).localeCompare(xmlFieldOptionDisplayLabel(b))
      )
      .slice(0, limit);
  }

  private leafFromPath(path: string): string {
    const parts = (path ?? '').split('/').filter(Boolean);
    return parts.length ? parts[parts.length - 1] : path;
  }

  private normalizeSearch(input: string): string {
    return (input ?? '')
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }
}
